import React, { useState } from "react";
import {
  CardTitle,
  CardDescription,
  CardHeader,
  CardContent,
  Card,
} from "@/components/shared/ui/card";
import { Label } from "@/components/shared/ui/label";
import { Input } from "@/components/shared/ui/input";
import { Button } from "@/components/shared/ui/button";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { TOO_MANY_ATTEMPTS, isThrottled, login } from "@/api/auth";
import { useMeQuery } from "@/queries/queries";
import { postLoginTarget } from "@/lib/postLoginHome";
import LoginForm from "@/pages/LoginForm";
import LoginFiling from "@/pages/LoginFiling";

const routeApi = getRouteApi("/login");

// --- Legacy layout (preserved verbatim, rendered when ?newLogin=1 is absent) ---
const LoginLegacy = () => {
  const navigate = useNavigate();
  const search: any = routeApi.useSearch();
  const meQuery = useMeQuery();
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // Prevent default form submission
    const formData = new FormData(event.currentTarget);
    const formDataObj = Object.fromEntries(formData.entries());
    const email = formDataObj.email as string;
    const password = formDataObj.password as string;
    await login(email, password)
      .then(async () => {
        // Refetch /me, then route to the user's platform home. Tracker-home
        // companies route through the /welcome warm-up splash; Classic stays
        // on /buildings. Centralized in postLoginTarget so all three login
        // entry points stay in lockstep (no splash flicker / race).
        const fresh = await meQuery.refetch();
        navigate(postLoginTarget(fresh.data, search?.redirect));
      })
      .catch((err) => {
        setErrorMessage(
          isThrottled(err)
            ? TOO_MANY_ATTEMPTS
            : "Unable to login with the provided username and password.",
        );
      });
  };

  return (
    <div className="bg-green-100 w-screen min-h-screen flex flex-col pb-[40px] px-4 sm:px-8 md:px-[60px]">
      <div className="py-4">
        <img src="/codeClearLogo.svg" alt="CodeClear" className="h-8" />
      </div>
      <div className="flex flex-row justify-center flex-wrap">
        <Card className="w-full max-w-sm sm:min-w-[350px]">
          <form onSubmit={handleSubmit}>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold">Login</CardTitle>
              <CardDescription>Enter your email and password</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    name="email"
                    id="email"
                    placeholder="m@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    name="password"
                    id="password"
                    required
                    type="password"
                  />
                  {errorMessage && (
                    <div className="text-red-500 text-sm mt-2">
                      {errorMessage}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <Link to="/external/password_reset_request">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Forgot Password?
                    </button>
                  </Link>
                </div>
                <Button className="w-full" type="submit">
                  Login
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
};

// --- New layout (rendered when ?newLogin=1 is set) ---
const LoginNew = () => {
  return (
    <div className="min-h-screen w-full flex flex-col items-center bg-muted px-4 pb-10">
      <div className="mt-10 mb-8 sm:mt-16 sm:mb-10 animate-in fade-in duration-500">
        <img src="/codeClearLogo.svg" alt="CodeClear" className="h-10" />
      </div>
      <Card className="w-full max-w-[440px] shadow-none border border-border rounded-xl p-8 animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out">
        <CardHeader className="px-0 pt-0 pb-6">
          <CardTitle className="text-2xl font-semibold tracking-tight">
            Sign in
          </CardTitle>
          <CardDescription>
            Sign in to file your NYC LL97 / LL88 compliance reports.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 pb-0 pt-0">
          <LoginForm />
        </CardContent>
      </Card>
    </div>
  );
};

// --- Feature-flag branch point ---
// Default: "The Filing" (LoginFiling) — the Claude Design handoff direction.
// Reverts, in case a regression surfaces before the rollout settles:
//   ?cardLogin=1   -> the prior card layout (LoginNew)
//   ?legacyLogin=1 -> the original green layout (LoginLegacy)
const Login = () => {
  const search = routeApi.useSearch() as {
    redirect?: string;
    legacyLogin?: string;
    cardLogin?: string;
  };
  if (search?.legacyLogin === "1") {
    return <LoginLegacy />;
  }
  if (search?.cardLogin === "1") {
    return <LoginNew />;
  }
  return <LoginFiling />;
};

export default Login;
